import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Trash2 } from "lucide-react";
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { api } from "@/lib/api";

const COLORS = ["#DC2626", "#2563EB", "#0B1D3A", "#16A34A", "#D97706", "#7C3AED"];
const day = (d) => new Date(d).toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "2-digit" });
const SRC = { medidas: "Por medidas", dieta: "Calculadora de dieta" };

const Chart = ({ horses }) => {
  const series = horses.map((h) => ({ ...h, data: h.weights.map((w) => ({ ts: new Date(w.date).getTime(), kg: w.kg })).sort((a, b) => a.ts - b.ts) }));
  return (
    <div className="h-72 border border-slate-200 rounded p-3 overflow-hidden" data-testid="horses-chart">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart margin={{ top: 10, right: 16, left: -8, bottom: 0 }}>
          <CartesianGrid stroke="#E2E8F0" strokeDasharray="3 3" />
          <XAxis dataKey="ts" type="number" scale="time" domain={["dataMin", "dataMax"]} tickFormatter={day} tick={{ fontSize: 11 }} allowDuplicatedCategory={false} />
          <YAxis dataKey="kg" unit=" kg" tick={{ fontSize: 11 }} domain={["dataMin - 20", "dataMax + 20"]} width={70} />
          <Tooltip labelFormatter={day} formatter={(v) => `${v} kg`} />
          <Legend />
          {series.map((h, i) => <Line key={h.id} data={h.data} dataKey="kg" name={h.name} stroke={COLORS[i % COLORS.length]} strokeWidth={2.5} dot={{ r: 4 }} />)}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

const HorseCard = ({ h, reload }) => {
  const list = [...h.weights].reverse();
  const diff = h.weights.length > 1 ? h.weights.at(-1).kg - h.weights.at(-2).kg : null;
  return (
    <div className="border border-slate-200 rounded p-5" data-testid={`horse-card-${h.id}`}>
      <div className="flex justify-between items-start">
        <div>
          <p className="font-display font-bold text-xl text-[#0B1D3A]">{h.name}</p>
          <p className="text-sm text-slate-600">Último peso: <b data-testid={`horse-last-${h.id}`}>{h.weights.at(-1)?.kg ?? "—"} kg</b>
            {diff !== null && <span className={`ml-2 font-bold ${diff > 0 ? "text-[#D97706]" : diff < 0 ? "text-[#2563EB]" : "text-slate-500"}`}>{diff > 0 ? "+" : ""}{diff} kg</span>}</p>
        </div>
        <button onClick={async () => { if (window.confirm(`¿Borrar ${h.name} y todo su historial?`)) { await api.delete(`/horses/${h.id}`); reload(); } }} className="text-slate-400 hover:text-[#DC2626]" data-testid={`horse-delete-${h.id}`}><Trash2 size={16} /></button>
      </div>
      <ul className="mt-3 divide-y divide-slate-100 max-h-56 overflow-auto">
        {list.map((w) => (
          <li key={w.id} className="flex items-center justify-between py-2 text-sm" data-testid={`horse-weight-${w.id}`}>
            <span className="text-slate-600">{day(w.date)} · {SRC[w.source]}</span>
            <span className="flex items-center gap-3"><b>{w.kg} kg</b>
              <button onClick={async () => { await api.delete(`/horses/${h.id}/weights/${w.id}`); reload(); }} className="text-slate-300 hover:text-[#DC2626]" data-testid={`horse-weight-delete-${w.id}`}><Trash2 size={14} /></button></span>
          </li>
        ))}
      </ul>
    </div>
  );
};

export const MyHorses = () => {
  const [horses, setHorses] = useState(null);
  const load = () => api.get("/horses").then((r) => setHorses(r.data));
  useEffect(() => { load(); }, []);
  if (!horses) return <p className="text-slate-500">Cargando…</p>;
  if (!horses.some((h) => h.weights.length)) return <p className="text-slate-500" data-testid="horses-empty">Aún no has guardado ningún peso. Usa <Link to="/peso" className="text-[#DC2626] font-bold">Peso del caballo</Link> o la <Link to="/calculadora" className="text-[#DC2626] font-bold">calculadora de dieta</Link> y pulsa «Guardar en mi cuenta».</p>;
  return (
    <div className="space-y-6" data-testid="my-horses">
      <Chart horses={horses.filter((h) => h.weights.length)} />
      <div className="grid sm:grid-cols-2 gap-4">{horses.map((h) => <HorseCard key={h.id} h={h} reload={load} />)}</div>
    </div>
  );
};
